import type {
    AssetWithArtworkUrl,
    DraggedAssetTransform,
    LabsElementaryGraph,
    LabsMeterEvent,
    LabsResourceEditableMetadata,
    LabsResourceID,
    LabsResourceType,
    TimeSignature,
    LabsMidiResourceMetadata,
    LabsJsonResourceMetadata,
    LabsAudioAssetMetadata,
    LabsResource,
    LabsAudioResource,
    LabsMidiResource,
    LabsJsonResource,
} from "@/client/core/platform-types/labs.api";
import type { AssetCategorySlug } from "@/client/core/platform-types/types";
import type { CoreStorage } from "@/client/core/services/storage.service";
import type { Shape } from "@/client/utils/geometry";
import type { OperatingSystem } from "@/client/utils/operating-system";
import type { BaseSignal, Listen, Signal } from "@/client/utils/signal";

/**
 * A SpliceGen model generation: the number people say out loud.
 *
 * Kept separate from the build within it, because a generation is what decides
 * how a release looks and where it sorts, while the build decides which Modal
 * function answers. `3.5` and `3.5-fast` are one generation, two builds.
 */
export type ModelGeneration = "2.0" | "2.5" | "3.0" | "3.5" | "4.0";

/** What an app page declares. Derived so the two can never drift. */
export type AppVersion = `v${ModelGeneration}`;

/**
 * One build of one generation, as the model picker lists it.
 *
 * `variant` is an open string rather than a union: a build's name is a name
 * ("fast"), it carries no behaviour here, and a new one should not need a type
 * change in core to appear in a picker. A generation does need the union —
 * that is what the colours and the ordering are keyed on.
 */
export interface ModelRelease {
    readonly generation: ModelGeneration;
    /** Absent for the plain build of a generation. */
    readonly variant?: string;
}

/**
 * Identifies a release within its capability: `"3.5"`, `"3.5-fast"`. Stable
 * enough to key a picker and a host by.
 */
export const getModelReleaseId = (release: ModelRelease): string =>
    release.variant === undefined ? release.generation : `${release.generation}-${release.variant}`;

/**
 * What the chip reads: `"3.5"`, `"3.5 fast"`.
 *
 * No `v`. In a chip beside the capability's name there is nothing else the
 * number could be, and the prefix cost a character of width in a panel that
 * has none to spare.
 */
export const getModelReleaseLabel = (release: ModelRelease): string =>
    release.variant === undefined ? release.generation : `${release.generation} ${release.variant}`;

/** The generation an app page's badge is stating. */
export const getAppVersionGeneration = (version: AppVersion): ModelGeneration =>
    kAppVersionGeneration[version];

const kAppVersionGeneration: Record<AppVersion, ModelGeneration> = {
    "v2.0": "2.0",
    "v2.5": "2.5",
    "v3.0": "3.0",
    "v3.5": "3.5",
    "v4.0": "4.0",
};

/**
 * Audience tiers. Membership is decided by LaunchDarkly flags (see
 * client/core/services/audience.ts); which apps a tier can see is declared
 * per-app via `AppDefinition.audiences`. `rnd` (the R&D team) is the top
 * tier: it sees every deployed app and is the default for new apps, so work
 * can ship to main for team review before being promoted to `internal`.
 */
export type Audience = "rnd" | "internal" | "soundcheck" | "partner";

export const kAllAudiences: readonly Audience[] = ["rnd", "internal", "soundcheck", "partner"];

/**
 * Anything whose visibility is decided by audience tiers.
 *
 * `AppDefinition` satisfies it, and so does a model release that has no page
 * of its own — a subversion offered only inside a capability's picker still
 * has to be gated, and there is no app for it to borrow the gating from.
 */
export interface AudienceGated {
    readonly audiences: readonly Audience[];
    /**
     * Optional LaunchDarkly flag override: when the flag evaluates true this
     * is visible regardless of audience tier.
     */
    readonly flagKey?: string;
}

export interface AppDemoMetadata {
    videoUrl?: string;
    notes?: string;
}

/**
 * A tab over the home page's Apps grid: what kind of tool an app is, or the
 * model family it was built around ("Lyria 4"). The tab order is
 * `kAppCategories` in `client/apps/AppDefinitions.ts`.
 */
export type AppCategory = "Tools" | "Edit" | "Arrange" | "Control" | "Lyria 4";

export interface AppDefinition {
    title: string;
    /** One-sentence summary shown on the home page and in the app menu. */
    description: string;
    directory: string;
    /**
     * Tiers that can see this app. Internal users see every deployed app
     * regardless of this list (rule lives in isAppVisible); internal-only
     * apps declare `audiences: ["internal"]` for readability.
     */
    audiences: readonly Audience[];
    author?: string;
    /**
     * The home tab an authored app sits under, besides All. Every app in
     * `kPeopleApps` needs one (`home-coverage.test.ts` checks), and every
     * release of one card shares it.
     */
    category?: AppCategory;
    /**
     * Further home tabs the app also sits under, beside `category`, such as
     * the model family it runs on. Every release of one card shares them.
     */
    extraCategories?: readonly AppCategory[];
    version?: AppVersion;
    /**
     * Optional per-app LaunchDarkly flag override: when the flag evaluates
     * true the app is visible regardless of audience tier.
     */
    flagKey?: string;
    demo?: AppDemoMetadata;
}

export enum AppEnvironment {
    Plugin = "Plugin",
    Website = "Website",
}

export interface AudioFileInfo {
    sampleRate: number;
    duration_s: number;
    numberOfChannels: number;
    bpm: number | null;
}

export type PlaybackState = { isPlaying: true; startOffset_s: number } | { isPlaying: false };

export type InputAudioType = AssetCategorySlug.Loop | AssetCategorySlug.Oneshot;

export interface InputFile<
    T_Type extends LabsResourceType = LabsResourceType,
    T_Metadata extends LabsResourceEditableMetadata = LabsResourceEditableMetadata,
> {
    readonly file: File;
    readonly type: T_Type;
    readonly metadata: T_Metadata | undefined;
}

export type InputFileAudio = InputFile<"wav", LabsAudioAssetMetadata>;
export type InputFileMidi = InputFile<"mid", LabsMidiResourceMetadata>;
export type InputFileJson = InputFile<"json", LabsJsonResourceMetadata>;

export interface AddedResource {
    id: LabsResourceID<"wav">;
    isAsset: boolean;
}

export type ResourceForType<T extends LabsResourceType> = T extends "wav"
    ? LabsAudioResource
    : T extends "mid"
      ? LabsMidiResource
      : T extends "json"
        ? LabsJsonResource
        : LabsResource;

export type ElementaryGraphFunction = (graph: Readonly<LabsElementaryGraph>) => void;

export interface PluginCommunication {
    sendElementaryGraph: ElementaryGraphFunction;
    addFiles: AddFiles;
    notifyFilesWereDropped: (files: File[]) => void;
    revealResourceOnDisk: RevealResourceOnDisk;
    openExternalLink: OpenExternalLink;
    getAllResources: RequestAllResources;
    deleteResources: DeleteResources;
    updateResourceMetadata: (
        id: LabsResourceID,
        metadata: LabsResourceEditableMetadata,
    ) => Promise<void>;
    getTransportState: GetTransportState;
}

export interface ElementaryService {
    setGraph: ElementaryGraphFunction;
}

export interface ResourcesService {
    requestAllResources: RequestAllResources;
    deleteResources: DeleteResources;
    updateResourceMetadata: UpdateResourceMetadata;
    addFiles: AddFiles;
    getAudioResource: GetAudioResource;
    getMidiResource: GetMidiResource;
    getJsonResource: GetJsonResource;
    revealOnDisk: RevealResourceOnDisk;
}

export interface MidiNoteEvent {
    noteNumber: number; // 0 - 127
    velocity: number; // 0 - 127
}

export interface MidiCCEvent {
    ccNumber: number; // 0–127
    value: number; // 0–127
}

export enum NotificationLevel {
    Debug = "Debug",
    Info = "Info",
    Warning = "Warning",
    Error = "Error",
    Success = "Success",
}

export interface UserNotification {
    level: NotificationLevel;
    title: string;
    body: string;
}

export interface DragInfo {
    readonly types: readonly string[];
}

export type ResourceIDToResource = Map<LabsResourceID["value"], LabsResource>;

export interface TransportBaseState {
    bpm: number;
    isPlaying: boolean;
    timeSignature: TimeSignature;
}

export interface TransportStateSnapshot extends TransportBaseState {
    position_beats: number;
}

export interface CoreState {
    isTestTonePlaying: boolean;
    dragInfo: DragInfo | undefined;
    windowShape: Shape;
    transport: TransportStateSnapshot;
    readonly resources: ResourceIDToResource;
    readonly notifications: UserNotification[];
}

/**
 * A drag in progress out of the host's catalog browser: the assets, plus what
 * the host did to the audio on its way over. `transform` is present when the
 * host is handing over a re-render (Transpose & Warp) — see
 * `DraggedAssetTransform`. Absent from older plugin builds and on the web,
 * where the filename is all we have to go on.
 */
export interface DraggedAssets {
    assets: AssetWithArtworkUrl[];
    transform?: DraggedAssetTransform;
}

// TODO convert more of these to BaseSignal?
// Flatten and automatically create CoreStreams?
export interface CoreSignals {
    midiNote: Signal<MidiNoteEvent>;
    midiCC: Signal<MidiCCEvent>;
    audioBuffer: Signal<AudioBuffer>;
    assetsDragStarted: Signal<DraggedAssets>;
    resources: Signal<ResourceIDToResource>;
    notification: Signal<UserNotification>;
    windowShape: Signal<Readonly<Shape>>;
    dragInfo: Signal<Readonly<DragInfo> | undefined>;
    transport: {
        bpm: BaseSignal<number>;
        isPlaying: BaseSignal<boolean>;
        timeSignature: BaseSignal<TimeSignature>;
        position_beats: BaseSignal<number>;
    };
    meterEvents: BaseSignal<LabsMeterEvent>;
}

export interface CoreStreams {
    midiNote: Listen<Readonly<MidiNoteEvent>>;
    midiCC: Listen<Readonly<MidiCCEvent>>;
    assetsDragStarted: Listen<DraggedAssets>;
    resources: Listen<Readonly<ResourceIDToResource>>;
    notification: Listen<Readonly<UserNotification>>;
    windowShape: Listen<Readonly<Shape>>;
    dragInfo: Listen<Readonly<DragInfo> | undefined>;
    transport: {
        bpm: Listen<number>;
        isPlaying: Listen<boolean>;
        timeSignature: Listen<TimeSignature>;
        position_beats: Listen<number>;
    };
    meterEvents: Listen<LabsMeterEvent>;
}

export type SetAudioGraph = (graph: LabsElementaryGraph) => void;

export interface PreviewPlayer {
    signal: Signal<PlaybackState>;
    start: (startOffset_s: number) => void;
    stop: () => void;
    getIsPlaying: () => boolean;
    getElapsed_s: () => number;
    getDuration_s: () => number;
}

export type DeleteResources = (ids: readonly LabsResourceID[]) => Promise<void>;
export type RequestAllResources = () => Promise<readonly LabsResource[]>;
export type RevealResourceOnDisk = (id: LabsResourceID) => void;
export type UpdateResourceMetadata = (
    id: LabsResourceID,
    update: Partial<LabsResourceEditableMetadata>,
) => Promise<void>;
export type GetTransportState = () => Promise<TransportStateSnapshot>;
export type GetAudioResource = (id: LabsResourceID<"wav">) => LabsAudioResource | undefined;
export type GetMidiResource = (id: LabsResourceID<"mid">) => LabsMidiResource | undefined;
export type GetJsonResource = (id: LabsResourceID<"json">) => LabsJsonResource | undefined;
export type CreatePreviewPlayer = (id: LabsResourceID<"wav">) => PreviewPlayer | undefined;
export type AddFiles = <T extends LabsResourceType>(
    files: InputFile<T>[],
) => Promise<ResourceForType<T>[] | Error>;
export type ShowUserNotification = (notification: Readonly<UserNotification>) => void;
export type OpenExternalLink = (url: string) => void;

export interface CoreActions {
    setAudioGraph: SetAudioGraph;
    stopAudio: () => void;
    startTestTone: () => void;
    stopTestTone: () => void;
    createPreviewPlayer: CreatePreviewPlayer;
    addFiles: AddFiles;
    revealResourceOnDisk: RevealResourceOnDisk;
    openExternalLink: OpenExternalLink;
    requestAllResources: RequestAllResources;
    getAudioResource: GetAudioResource;
    getMidiResource: GetMidiResource;
    getJsonResource: GetJsonResource;
    deleteResources: DeleteResources;
    updateResourceMetadata: UpdateResourceMetadata;
    showUserNotification: ShowUserNotification;
    getTransportState: GetTransportState;
}

export interface Core {
    readonly appDefinition: AppDefinition;
    readonly environment: AppEnvironment;
    readonly operatingSystem: OperatingSystem;
    readonly actions: CoreActions;
    readonly state: CoreState;
    readonly streams: CoreStreams;
}

export interface AppContext<
    T_AppSpecificContext extends object = object,
    T_AppSpecificState = unknown,
> {
    readonly core: Core;
    app: T_AppSpecificContext;
    storage?: CoreStorage<T_AppSpecificState>;
}
